import { useQuery, useQueries } from '@tanstack/react-query';
import type { ByRegion, CountryProfile, Distribution, FiltersQuery, Meta, Ranking, Summary, Trend } from '@upbeat/shared';
import { getJson } from './client';

const FIVE_MIN = 5 * 60_000; // matches the API's Cache-Control max-age

export function useMeta() {
  return useQuery({
    queryKey: ['meta'],
    queryFn: ({ signal }) => getJson<Meta>('meta', undefined, signal),
    staleTime: Infinity,
  });
}

function useData<T>(path: string, q: FiltersQuery | null) {
  return useQuery({
    queryKey: [path, q],
    queryFn: ({ signal }) => getJson<T>(path, q!, signal),
    enabled: q !== null,
    staleTime: FIVE_MIN,
    placeholderData: (prev) => prev, // keep the last result while filters change
  });
}

export const useSummary = (q: FiltersQuery | null) => useData<Summary>('summary', q);
export const useByRegion = (q: FiltersQuery | null) => useData<ByRegion>('by-region', q);
export const useDistribution = (q: FiltersQuery | null) => useData<Distribution>('distribution', q);
export const useTrend = (q: FiltersQuery | null) => useData<Trend>('trend', q);
export const useRanking = (q: FiltersQuery | null) => useData<Ranking>('ranking', q);


export function useCountryProfile(country: string | null, year?: number | null) {
  return useQuery({
    queryKey: ['country-profile', country, year],
    queryFn: ({ signal }) => {
      const params: Record<string, unknown> = { country: country! };
      if (year) params.year = year;
      return getJson<CountryProfile>('country-profile', params, signal);
    },
    enabled: Boolean(country),
    staleTime: FIVE_MIN,
  });
}

export function useMultiCountryProfiles(countries: string[], year?: number | null) {
  return useQueries({
    queries: countries.map((country) => ({
      queryKey: ['country-profile', country, year],
      queryFn: ({ signal }: { signal: AbortSignal }) => {
        const params: Record<string, unknown> = { country };
        if (year) params.year = year;
        return getJson<CountryProfile>('country-profile', params, signal);
      },
      enabled: Boolean(country),
      staleTime: FIVE_MIN,
    })),
  });
}
